import type { SolutionSubpageConfig, SolutionSubpageSlug } from './types';
import { solutionPremiumSections } from './premiumSections';
import { solutionPageExtras } from './solutionPageExtras';

const partnerCta = { label: 'Partner With Us', to: '/partner-registration' } as const;

const partnershipProcess = [
  {
    step: 1,
    title: 'Partnership onboarding',
    description: 'We assess your market and create a customized go-to-market plan.',
    bullets: ['Business assessment', 'Custom platform setup', 'Vendor network access'],
  },
  {
    step: 2,
    title: 'Platform integration',
    description: 'White-label setup, branding, and team training.',
    bullets: ['Platform customization', 'Team training', 'Hardware alignment'],
  },
  {
    step: 3,
    title: 'Launch & support',
    description: 'Go live with your branded solution and ongoing partnership.',
    bullets: ['Client onboarding', 'Marketing support', 'Ongoing partnership'],
  },
] as const;

export const solutionSubpages: Record<SolutionSubpageSlug, SolutionSubpageConfig> = {
  'smart-house': {
    slug: 'smart-house',
    premium: solutionPremiumSections['smart-house'],
    pageExtras: solutionPageExtras['smart-house'],
    path: '/smart-house-real-estate',
    breadcrumbLabel: 'Smart House',
    hero: {
      title: 'Smart House Solutions',
      subtitle:
        'Partner with us to offer cutting-edge smart home technology. We provide the platform — you deliver the resident experience.',
      primaryCta: partnerCta,
    },
    heroImage: {
      src: '/images/solutions/smart-house/banner-image-smart-house.png',
      alt: 'Smart house connected home platform visualization',
      width: 2266,
      height: 1856,
    },
    heroBackdrop: {
      src: '/images/solutions/smart-house/banner-image-smart-house.png',
      kind: 'image',
      alt: 'Smart house connected home platform visualization',
    },
    advantages: {
      eyebrow: 'ADVANTAGES',
      title: 'Smart Living Made Easy for Property Partners',
      items: [
        {
          icon: 'home',
          title: 'White-Label Platform',
          description: 'Launch a branded smart residential solution without building software in-house.',
        },
        {
          icon: 'shield',
          title: 'Secure Access Control',
          description: 'Keyless entry, visitor management, and property-wide access from one portal.',
        },
        {
          icon: 'zap',
          title: 'Asset Protection',
          description: 'Connected sensors and automation reduce maintenance costs and risk.',
        },
        {
          icon: 'chart',
          title: 'Revenue Growth',
          description: 'Premium smart amenities help increase rental value and resident retention.',
        },
      ],
    },
    sections: {
      eyebrow: 'SOLUTIONS',
      title: 'Smart residential offerings for every property type',
      subtitle: 'Purpose-built modules for multifamily, student housing, and premium residences.',
    },
    offerings: [
      {
        icon: 'shield',
        title: 'Access Control',
        description: 'Property-wide control for managers, residents, and staff through smart access devices.',
        whyItMatters: 'Secure seamless access from front door to amenities via your branded app.',
        targetMarket: 'Property managers, apartment complexes, and residential communities',
        image: {
          src: '/images/solutions/smart-house/access-control-smart-home.png',
          alt: 'Smart access control for residential properties',
          width: 1200,
          height: 800,
        },
      },
      {
        icon: 'zap',
        title: 'Asset Protection',
        description: 'Smart sensors and thermostats for preventative maintenance and safety monitoring.',
        whyItMatters: 'Protect tenants and property while reducing emergency maintenance spend.',
        targetMarket: 'Property owners, facility managers, and maintenance companies',
        image: {
          src: '/images/solutions/smart-house/asset-protection.png',
          alt: 'Asset protection sensors and monitoring for smart homes',
          width: 1200,
          height: 800,
        },
      },
      {
        icon: 'home',
        title: 'Smart Apartment',
        description: 'Premium in-unit automation that increases satisfaction and long-term retention.',
        whyItMatters: 'Smart amenities can increase rental premiums by 5–30% in multifamily housing.',
        targetMarket: 'Developers, property managers, and homebuilders',
        image: {
          src: '/images/solutions/smart-house/smart-apartment.png',
          alt: 'Smart apartment in-unit automation',
          width: 1200,
          height: 800,
        },
      },
    ],
    process: partnershipProcess,
    stats: [
      { value: '5–30%', label: 'Rental premium lift' },
      { value: '50+', label: 'Certified vendors' },
      { value: '24/7', label: 'Partner support' },
      { value: '99.9%', label: 'Platform uptime' },
    ],
    capabilities: {
      eyebrow: 'CAPABILITIES',
      title: 'Everything partners need to run smart residential programs',
      items: [
        { icon: 'lock', title: 'Smart Locks & Access', description: 'Keyless entry with role-based permissions.' },
        { icon: 'lightbulb', title: 'Lighting & Climate', description: 'Schedules, scenes, and energy-aware controls.' },
        { icon: 'shield', title: 'Security Sensors', description: 'Leak, motion, and door/window monitoring.' },
        { icon: 'smartphone', title: 'Resident Mobile App', description: 'White-label iOS and Android experiences.' },
        { icon: 'layers', title: 'Property Dashboard', description: 'Multi-tenant admin for portfolios and sites.' },
        { icon: 'wifi', title: 'Device Provisioning', description: 'SmartConfig, AP mode, and bulk onboarding.' },
      ],
    },
    faqs: {
      eyebrow: 'FAQs',
      title: 'Frequently asked questions',
      items: [
        {
          question: 'How fast can we launch a branded smart residential program?',
          answer:
            'Most partners launch in weeks — we align devices, configure your branded portal and mobile app, and roll out building-by-building with onboarding support.',
        },
        {
          question: 'Can we integrate with existing property management systems?',
          answer:
            'Yes. Use our APIs and integrations to sync residents, units, access roles, and operational workflows with your existing PMS and leasing tools.',
        },
        {
          question: 'Do you support multiple device vendors?',
          answer:
            'Yes. We support a certified vendor ecosystem across locks, sensors, climate devices, and gateways so you can build the right package for each property type.',
        },
        {
          question: 'Who owns the brand experience?',
          answer:
            'You do. The resident app, portal, and communications can be white-labeled with your logo, colors, and domain — we operate the platform underneath.',
        },
      ],
    },
    closing: {
      titleLines: ['Ready to Partner', 'With Us?'],
      subtitle: 'Join real estate professionals offering branded smart home solutions.',
      ctaLabel: 'Become a Partner',
    },
  },
  'smart-hospitality': {
    slug: 'smart-hospitality',
    premium: solutionPremiumSections['smart-hospitality'],
    pageExtras: solutionPageExtras['smart-hospitality'],
    path: '/smart-hospitality',
    breadcrumbLabel: 'Smart Hospitality',
    hero: {
      title: 'Smart Hospitality',
      subtitle:
        'Deliver memorable guest experiences with white-label smart room technology, energy automation, and centralized operations.',
      primaryCta: partnerCta,
    },
    heroImage: {
      src: '/images/solutions/smart-hospitality/smart-hospitality-banner-image.png',
      alt: 'Smart hospitality solutions for hotels',
      width: 1400,
      height: 900,
    },
    heroBackdrop: {
      src: '/images/solutions/smart-hospitality/smart-hospitality-banner-image.png',
      kind: 'image',
      alt: 'Luxury hotel lobby with smart hospitality technology',
    },
    advantages: {
      eyebrow: 'ADVANTAGES',
      title: 'Transform Every Guest Stay with Intelligent Hospitality Automation',
      subtitle:
        'Deliver seamless guest experiences, reduce operational costs, and manage every property from one connected IoT platform.',
      kpis: [
        { icon: 'wifi', value: '500+ Rooms Connected' },
        { icon: 'zap', value: '35% Energy Savings' },
        { icon: 'chart', value: '40% Better Guest Engagement' },
        { icon: 'activity', value: '24/7 Property Monitoring' },
      ],
      items: [
        {
          icon: 'key',
          title: 'Contactless Guest Journey',
          description:
            'Mobile check-in, digital keys, and personalized room settings create a seamless arrival experience.',
          metricBadge: '↓ 60% Check-in Time',
        },
        {
          icon: 'zap',
          title: 'Smart Energy Optimization',
          description:
            'Occupancy-based HVAC, lighting automation, and usage insights help reduce utility costs.',
          metricBadge: '↓ 35% Energy Costs',
        },
        {
          icon: 'smartphone',
          title: 'Branded Guest Experience',
          description:
            'Offer a white-label guest app for room controls, concierge requests, and service bookings.',
          metricBadge: '↑ 40% Guest Engagement',
        },
        {
          icon: 'chart',
          title: 'Real-Time Property Intelligence',
          description:
            'Monitor room status, device health, alerts, occupancy, and staff operations from one dashboard.',
          metricBadge: '24/7 Monitoring',
        },
        {
          icon: 'settings',
          title: 'Smart Staff Operations',
          description:
            'Automate housekeeping, maintenance alerts, and staff coordination in real time.',
          metricBadge: '↓ 50% Manual Tasks',
        },
      ],
    },
    sections: {
      eyebrow: 'SOLUTIONS',
      title: 'Hospitality solutions built for hotels and resorts',
      subtitle: 'From boutique properties to global chains — one platform, your brand.',
    },
    offerings: [
      {
        icon: 'shield',
        title: 'Guest Access Control',
        description: 'Keyless entry, mobile room keys, and secure staff access zones.',
        whyItMatters: 'Faster check-in and a premium, contactless guest experience.',
        targetMarket: 'Hotels, resorts, vacation rentals, and hospitality operators',
      },
      {
        icon: 'zap',
        title: 'Energy Management',
        description: 'Smart thermostats, lighting, and occupancy-based automation.',
        whyItMatters: 'Reduce operational costs by up to 30% while improving guest comfort.',
        targetMarket: 'Hotel chains, resort operators, and facility managers',
      },
      {
        icon: 'hotel',
        title: 'Smart Room Experience',
        description: 'In-room controls, entertainment, and personalized amenity automation.',
        whyItMatters: 'Smart rooms can lift guest satisfaction scores and support premium rates.',
        targetMarket: 'Luxury hotels, boutique properties, and upscale venues',
      },
    ],
    process: partnershipProcess,
    stats: [
      { value: '30%', label: 'Energy savings' },
      { value: '50+', label: 'Certified vendors' },
      { value: '24/7', label: 'Partner support' },
      { value: '99.9%', label: 'Platform uptime' },
    ],
    capabilities: {
      eyebrow: 'CAPABILITIES',
      title: 'Platform capabilities for modern hospitality',
      subtitle:
        'Explore the core platform modules operators use to deliver connected guest experiences at scale.',
      items: [
        {
          icon: 'key',
          title: 'Digital Room Keys',
          description:
            'Deliver secure, contactless room access through branded mobile credentials integrated with PMS and front-desk workflows.',
          bullets: [
            'Reservation-linked credentials with time-bound access',
            'Instant revoke and reissue when stays change',
            'Staff approval controls for sensitive operations',
          ],
        },
        {
          icon: 'lightbulb',
          title: 'Room Automation',
          description:
            'Orchestrate welcome, sleep, and checkout scenes that delight guests while cutting in-room energy waste.',
          bullets: [
            'Pre-built scenes for every room category',
            'Occupancy-aware HVAC and lighting rules',
            'Automatic checkout reset on departure',
          ],
        },
        {
          icon: 'activity',
          title: 'Staff Operations',
          description:
            'Give housekeeping and maintenance real-time room status, alerts, and routing from one operations view.',
          bullets: [
            'Live housekeeping status synced with PMS',
            'Maintenance alerts with room context',
            'Priority routing and SLA tracking',
          ],
        },
        {
          icon: 'smartphone',
          title: 'Guest Mobile App',
          description:
            'Offer a fully branded guest app for controls, service requests, and messaging across every property.',
          bullets: [
            'White-label UI matching your brand identity',
            'In-stay controls and concierge requests',
            'Consistent experience across portfolios',
          ],
        },
        {
          icon: 'chart',
          title: 'Energy Analytics',
          description:
            'Monitor consumption at room and portfolio level with dashboards built for hospitality and ESG reporting.',
          bullets: [
            'Per-room trends and anomaly detection',
            'Portfolio rollups for sustainability metrics',
            'Benchmarking across properties and seasons',
          ],
        },
        {
          icon: 'shield',
          title: 'Enterprise Security',
          description:
            'Protect guest data with encryption, role-based access, and audit-ready controls for hotel groups.',
          bullets: [
            'Encrypted guest data and secure key delivery',
            'Role-based access for staff and partners',
            'Audit trails, SSO, and compliance reporting',
          ],
        },
      ],
    },
    faqs: {
      eyebrow: 'FAQs',
      title: 'Frequently asked questions',
      items: [
        {
          question: 'Can guests use digital keys without a front desk check-in?',
          answer:
            'Yes. You can enable mobile check-in flows and issue time-bound credentials automatically, or keep front-desk controls depending on your operating model.',
        },
        {
          question: 'How does room automation work with occupancy?',
          answer:
            'Automations can use door sensors, motion, and schedules to adjust HVAC and lighting while preserving comfort and reducing energy waste.',
        },
        {
          question: 'Can we brand the guest experience end-to-end?',
          answer:
            'Yes. The guest app and operations portal can be white-labeled to match your brand and guest journey.',
        },
        {
          question: 'Is the platform suitable for multi-property hotel groups?',
          answer:
            'Yes. Multi-tenant dashboards and role-based access support portfolios with centralized operations and property-level teams.',
        },
      ],
    },
    closing: {
      titleLines: ['Ready to Partner', 'With Us?'],
      subtitle: 'Launch your branded smart hospitality platform with Aeviot.',
      ctaLabel: 'Become a Partner',
    },
  },
  'smart-manufacturing': {
    slug: 'smart-manufacturing',
    premium: solutionPremiumSections['smart-manufacturing'],
    pageExtras: solutionPageExtras['smart-manufacturing'],
    path: '/smart-manufacturing',
    breadcrumbLabel: 'Smart Manufacturing',
    hero: {
      title: 'Manufacturing analytics and factory automation',
      subtitle:
        'Easily integrate our Industrial IoT solution with your manufacturing equipment and shop floor systems. Automate factory operations, unlock visibility, and gain actionable insights from real-time data.',
      primaryCta: { label: 'Get Started', to: '/partner-registration' },
    },
    heroImage: {
      src: '/images/solutions/smart-manufacturing/smart-manufacturing-banner-image.png',
      alt: 'Smart manufacturing factory automation',
      width: 1400,
      height: 900,
    },
    heroBackdrop: {
      src: 'https://smarthomedev.s3.ap-south-1.amazonaws.com/websites-assets/smart-manufacturing-banner.mp4',
      kind: 'video',
      type: 'video/mp4',
    },
    advantages: {
      eyebrow: 'ADVANTAGES',
      title: 'Make Any Product Smart — Faster and at Lower Cost',
      items: [
        {
          icon: 'factory',
          title: 'No IoT Team Required',
          description: 'Firmware, cloud, and apps provided — focus on manufacturing and distribution.',
          image: {
            src: '/images/solutions/smart-manufacturing/discrete-manufacturing.png',
            alt: 'Connected factory floor with smart manufacturing systems',
            width: 1200,
            height: 800,
          },
          highlights: [
            'Pre-built firmware SDK for ESP32 and ESP8266',
            'Managed MQTT cloud with device twins and fleet tools',
            'White-label iOS and Android apps included',
            'OTA updates and remote diagnostics out of the box',
          ],
        },
        {
          icon: 'refresh',
          title: '3-Month Launch Path',
          description: 'Structured program from prototype to production-ready smart SKU.',
          image: {
            src: '/images/solutions/smart-manufacturing/process-manufacturing.png',
            alt: 'Manufacturing launch timeline from prototype to production',
            width: 1200,
            height: 800,
          },
          highlights: [
            'Weeks 1–2: hardware assessment and SDK integration',
            'Weeks 3–6: branded cloud, dashboard, and mobile apps',
            'Weeks 7–10: pilot production, QA, and field validation',
            'Weeks 11–12: mass manufacturing handoff and launch support',
          ],
        },
        {
          icon: 'layers',
          title: 'Your Brand, Your Product',
          description: 'Complete white-label stack customers perceive as yours.',
          image: {
            src: '/images/solutions/smart-manufacturing/industries-image.png',
            alt: 'White-label smart manufacturing platform under your brand',
            width: 1400,
            height: 900,
          },
          highlights: [
            'Your logos, colors, and app store presence',
            'Partner portal and APIs under your brand',
            'Customer apps with no vendor branding',
            'Consistent experience across device, app, and cloud',
          ],
        },
        {
          icon: 'chart',
          title: 'Lower Dev Costs',
          description: 'Save up to 70–80% versus building connectivity from scratch.',
          image: {
            src: '/images/solutions/smart-manufacturing/smart-manufacturing-banner-image.png',
            alt: 'Cost-efficient smart manufacturing connectivity platform',
            width: 1400,
            height: 900,
          },
          highlights: [
            'Skip 12–18 months of in-house IoT engineering',
            'Shared platform reduces infrastructure spend',
            'Predictable licensing instead of open-ended R&D',
            '70–80% savings versus a greenfield connectivity stack',
          ],
        },
      ],
    },
    sections: {
      eyebrow: 'SOLUTIONS',
      title: 'From legacy product to connected device',
      subtitle: 'A proven transformation path with firmware, cloud, and mobile apps included.',
    },
    offerings: [],
    process: [
      {
        step: 1,
        title: 'Product analysis & integration',
        description: 'Assess compatibility and integrate smart connectivity components.',
        bullets: ['Compatibility assessment', 'Firmware customization', 'Prototype validation'],
      },
      {
        step: 2,
        title: 'Platform & app development',
        description: 'Configure branded cloud infrastructure and mobile applications.',
        bullets: ['White-label cloud setup', 'Mobile app branding', 'Device management portal'],
      },
      {
        step: 3,
        title: 'Production launch',
        description: 'Scale manufacturing with QA, documentation, and go-to-market support.',
        bullets: ['Production setup', 'Quality assurance', 'Launch strategy'],
      },
    ],
    stats: [
      { value: '3 months', label: 'Typical launch' },
      { value: '70%', label: 'Cost savings' },
      { value: '99.9%', label: 'Platform uptime' },
      { value: '24/7', label: 'Technical support' },
    ],
    capabilities: {
      eyebrow: 'CAPABILITIES',
      title: 'Complete product-to-smart technology stack',
      items: [
        {
          icon: 'cpu',
          title: 'Universal Firmware SDK',
          description:
            'Production-ready ESP32 and ESP8266 libraries with MQTT, OTA, provisioning, and device twin sync — so your team ships connected firmware in weeks, not quarters.',
        },
        {
          icon: 'cloud',
          title: 'IoT Cloud Platform',
          description:
            'Managed MQTT broker, digital twins, multi-tenant fleet management, and role-based access — all white-labeled under your brand with enterprise-grade uptime.',
        },
        {
          icon: 'smartphone',
          title: 'White-Label Apps',
          description:
            'Branded iOS and Android control apps with your logo, colors, and flows — ready for app store submission with no Aeviot branding in the resident experience.',
        },
        {
          icon: 'chart',
          title: 'Product Analytics',
          description:
            'Usage insights, health monitoring, failure diagnostics, and exportable reports that help product, support, and engineering teams act on real field data.',
        },
        {
          icon: 'refresh',
          title: 'OTA Updates',
          description:
            'Staged rollouts, delta patches, automatic rollback, and full audit trails — deploy firmware improvements safely across your entire device fleet.',
        },
        {
          icon: 'wifi',
          title: 'Provisioning Tools',
          description:
            'SmartConfig, AP mode, bulk credential injection, and factory-line tooling designed for high-volume manufacturing and repeatable production QA.',
        },
      ],
    },
    faqs: {
      eyebrow: 'FAQs',
      title: 'Frequently asked questions',
      items: [
        {
          question: 'What is the role of IoT in manufacturing processes?',
          answer:
            'IoT improves efficiency and predictability by connecting equipment, collecting real-time data, and enabling analytics and automation across the shop floor.',
        },
        {
          question: 'Can we start with one line and expand later?',
          answer:
            'Yes. Start with a pilot asset or line, validate KPIs, then scale to additional machines, lines, and plants using the same platform.',
        },
        {
          question: 'Do you support legacy machines and protocols?',
          answer:
            'Yes. Gateways, edge agents, and connectors help integrate both modern and legacy equipment across common industrial protocols.',
        },
        {
          question: 'How do you handle data security?',
          answer:
            'We support secure transport, tenant isolation, access controls, and audit trails — and we can align with your network and compliance requirements.',
        },
      ],
    },
    closing: {
      titleLines: ['Ready to Build', 'Smart Products?'],
      subtitle: 'Join manufacturers shipping connected devices on the Aeviot platform.',
      ctaLabel: 'Start Manufacturing',
    },
  },
  'commercial-lighting': {
    slug: 'commercial-lighting',
    premium: solutionPremiumSections['commercial-lighting'],
    pageExtras: solutionPageExtras['commercial-lighting'],
    path: '/smart-commercial-lighting',
    breadcrumbLabel: 'Commercial Lighting',
    hero: {
      title: 'Intelligent Lighting Control for Modern Commercial Spaces',
      subtitle:
        'Automate lighting, reduce energy costs, and manage every building from one connected IoT platform.',
      primaryCta: partnerCta,
    },
    heroImage: {
      src: '/images/solutions/smart-commercial-lighting/commercial-banner-image.png',
      alt: 'Smart commercial lighting control platform',
      width: 1400,
      height: 900,
    },
    heroBackdrop: {
      src: '/images/solutions/smart-commercial-lighting/commercial-banner-image.png',
      kind: 'image',
      alt: 'Commercial office building with intelligent lighting',
    },
    advantages: {
      eyebrow: 'ADVANTAGES',
      title: 'Smarter Lighting for Commercial Spaces',
      items: [],
    },
    sections: {
      eyebrow: 'SOLUTIONS',
      title: 'Lighting solutions for every commercial vertical',
      subtitle: 'Office, industrial, and retail deployments on one white-label platform.',
    },
    offerings: [
      {
        icon: 'building',
        title: 'Office Building Automation',
        description: 'Occupancy-based lighting and scheduling for productive workspaces.',
        whyItMatters: 'Lower energy spend while improving employee comfort and focus.',
        targetMarket: 'Office buildings, corporate campuses, and commercial real estate',
      },
      {
        icon: 'factory',
        title: 'Industrial Lighting Control',
        description: 'Robust controls for warehouses, plants, and high-bay environments.',
        whyItMatters: 'Improve safety and efficiency with automated zone-based lighting.',
        targetMarket: 'Manufacturing plants, warehouses, and distribution centers',
      },
      {
        icon: 'lightbulb',
        title: 'Retail & Hospitality Lighting',
        description: 'Dynamic scenes, color temperature, and mood lighting for guest experiences.',
        whyItMatters: 'Lighting can lift retail sales 15–30% and enhance venue ambiance.',
        targetMarket: 'Retail stores, restaurants, hotels, and entertainment venues',
      },
    ],
    process: partnershipProcess,
    stats: [
      { value: '60%', label: 'Energy savings' },
      { value: '24/7', label: 'Monitoring' },
      { value: '5+', label: 'Years platform' },
      { value: '99.9%', label: 'Uptime guarantee' },
    ],
    capabilities: {
      eyebrow: 'CAPABILITIES',
      title: 'Commercial lighting platform capabilities',
      items: [
        { icon: 'lightbulb', title: 'Smart LED Controls', description: 'Panels, drivers, and dimming zones.' },
        { icon: 'activity', title: 'Occupancy Sensors', description: 'Motion-based automation and vacancy off.' },
        { icon: 'globe', title: 'Daylight Harvesting', description: 'Adjust output based on ambient light.' },
        { icon: 'layers', title: 'Scene Scheduling', description: 'Time, calendar, and event-based rules.' },
        { icon: 'chart', title: 'Energy Dashboards', description: 'Portfolio analytics and reporting.' },
        { icon: 'settings', title: 'Remote Maintenance', description: 'Fault alerts and firmware updates.' },
      ],
    },
    faqs: {
      eyebrow: 'FAQs',
      title: 'Frequently asked questions',
      items: [
        {
          question: 'Can we retrofit existing commercial lighting infrastructure?',
          answer:
            'Yes. We support retrofit approaches that reuse existing fixtures where possible, plus gateways/controllers to bring them online.',
        },
        {
          question: 'How do schedules and scenes work across large sites?',
          answer:
            'You can create templates per building, floor, or zone and apply them across sites, with overrides for events or occupancy-driven automation.',
        },
        {
          question: 'Do you provide monitoring and fault alerts?',
          answer:
            'Yes. Get alerts for device failures, energy anomalies, and compliance checks, with dashboards for technicians and managers.',
        },
        {
          question: 'Can we integrate with building management systems?',
          answer:
            'Yes. Use APIs and connectors to integrate lighting controls with BMS workflows and other facility systems.',
        },
      ],
    },
    closing: {
      titleLines: ['Ready to Build', 'Smarter Spaces?'],
      subtitle:
        'Launch a branded smart lighting platform for offices, retail, industrial, and multi-site commercial environments.',
      ctaLabel: 'Partner With Us',
    },
  },
};

export function getSolutionSubpage(slug: SolutionSubpageSlug): SolutionSubpageConfig {
  return solutionSubpages[slug];
}
